// Pure text builders for the invoiced-by-month chart: the tooltip line and
// the chart's spoken label.
//
// They sit in their own leaf module because Recharts never calls a tooltip
// renderer under jsdom's zero-width ResponsiveContainer, so wording left
// inside the chart component would be untestable.
//
// Two rules run through the wording. Money is stated at full precision here —
// the axis abbreviates because a tick has no room, but the figure a reader
// hovers for is the exact one, and it always carries its GST basis. And the
// approved amount is "approved", never "paid": invoice statuses stop at
// approved and the system holds no settlement record.

import type { FinancialMonthRow, FinancialReportModel } from "../../../../types/report";
import { formatAud2dp } from "../../../../utils/formatCurrency";
import { formatReportMonth } from "../reportFormat";

type GstBasis = FinancialReportModel["gstBasis"];

function invoiceCount(count: number): string {
  return `${count} ${count === 1 ? "invoice" : "invoices"}`;
}

/** One line, e.g. "Jul 2026 · 2 invoices · $58,900.00 invoiced ·
 *  $40,000.00 approved ex-GST". A month nobody billed in says so rather than
 *  reciting three noughts. */
export function formatInvoicedTooltip(
  row: FinancialMonthRow,
  gstBasis: GstBasis,
): string {
  const month = formatReportMonth(row.month);
  if (row.invoiceCount === 0) return `${month} · no invoices`;
  return (
    `${month} · ${invoiceCount(row.invoiceCount)} · ` +
    `${formatAud2dp(row.invoicedTotal)} invoiced · ` +
    `${formatAud2dp(row.approvedTotal)} approved ${gstBasis}`
  );
}

/** The chart's `aria-label`: the window it covers, what was billed across it,
 *  and where the per-month figures are. The detail table below carries every
 *  month, so the label deliberately does not read out twelve of them. */
export function describeInvoicedChart(
  series: FinancialMonthRow[],
  gstBasis: GstBasis,
): string {
  const first = formatReportMonth(series[0].month);
  const last = formatReportMonth(series[series.length - 1].month);
  const total = series.reduce((sum, row) => sum + row.invoicedTotal, 0);
  const count = series.reduce((sum, row) => sum + row.invoiceCount, 0);

  if (count === 0) {
    return (
      `Invoiced by month, ${first} to ${last}, no invoices in this window. ` +
      `Exact monthly figures follow in the monthly detail table.`
    );
  }

  // The tallest column is most of what a sighted reader takes from the shape
  // of the chart, so the label says which month it is.
  const peak = series.reduce((highest, row) =>
    row.invoicedTotal > highest.invoicedTotal ? row : highest,
  );

  return (
    `Invoiced by month, ${first} to ${last}, ${formatAud2dp(total)} invoiced ` +
    `${gstBasis} across ${invoiceCount(count)}, highest ` +
    `${formatReportMonth(peak.month)} at ${formatAud2dp(peak.invoicedTotal)}. ` +
    `Exact monthly figures follow in the monthly detail table.`
  );
}
